/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useState } from "react";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useMainBannerStore } from "@/stores/useMainBannerStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { colors } from "@dotnet/telematics-design-system";

import { ROUTES, useMediaQueries, VEHICLES_ACTIONS } from "../../../../shared";
import useGeneralHeights from "../../../../shared/hooks/useGeneralHeights";
import Separator from "../../../Separator/Separator";
import { DESKTOP_DETAILS_MARGIN_TOP } from "../../constants";
import CompaniesDetailsContainer from "./components/CompaniesDetailsContainer/CompaniesDetailsContainer";
import DetailsTitleArea from "./components/DetailsTitleArea";
import useDetailsContent from "./hooks/useDetailsContent";

interface IDetailsContentProps {
  route: ROUTES;
  onClose?: () => void;
}

const DetailsContent = ({ route, onClose }: IDetailsContentProps) => {
  const { fromMd } = useMediaQueries();

  const mainBannerHeight = useMainBannerStore(state => state.height);

  const {
    renderFileSyncContent,
    renderConnectionSettings,
    renderGeneralDetails,
    renderSchedules,
    renderCompanyCards,
    renderEmailTransferContent,
    renderCompanySettings,
    renderActivateVehicleBanner
  } = useDetailsContent(route);

  const { NAVBAR_HEIGHT } = useGeneralHeights(mainBannerHeight);

  const {
    loading: isTableLoading,
    detailsData,
    inlineActions
  } = useTableStoreByRoute(route);

  const isDetailsLoading = useDetailsStore(state => state.isLoading);

  const [titleAreaHeight, setTitleAreaHeight] = useState<number | null>(0);

  const companiesPage = route === ROUTES.Companies;

  const desktopDetailsTitleAreHeight = `calc(${titleAreaHeight}px - ${DESKTOP_DETAILS_MARGIN_TOP})`;

  const displayLoader = isDetailsLoading || isTableLoading;
  const detailsContentHeightToMd = `calc(100vh - ${desktopDetailsTitleAreHeight} - ${NAVBAR_HEIGHT})`;

  const detailsContentHeightFromMd = `calc(100vh - ${desktopDetailsTitleAreHeight}  - ${NAVBAR_HEIGHT} - ${DESKTOP_DETAILS_MARGIN_TOP})`;
  const tabsAreaHeight = "60px"; // 44px of tabs height and 16px margin top to tabs area

  // company details content does not have margin bottom
  const companyDetailsContentHeight = `calc(100vh - ${desktopDetailsTitleAreHeight}  - ${NAVBAR_HEIGHT} - ${DESKTOP_DETAILS_MARGIN_TOP} - ${tabsAreaHeight})`;

  const calculateDetailsContentHeight = () => {
    return fromMd ? detailsContentHeightFromMd : detailsContentHeightToMd;
  };

  const hasEditVehicle =
    route === ROUTES.Vehicles &&
    inlineActions.some(action => action.title === VEHICLES_ACTIONS.EditVehicle);

  const shouldRenderActivateBanner =
    hasEditVehicle && detailsData?.disabled && !isDetailsLoading;

  return (
    <div
      css={css({
        width: `100%`,
        height: "100%"
      })}
    >
      <DetailsTitleArea
        route={route}
        displayLoader={displayLoader}
        detailsData={detailsData}
        onClose={onClose}
        setTitleAreaHeight={setTitleAreaHeight}
      />
      <Separator color={colors.gray200} />

      {companiesPage && detailsData && (
        <CompaniesDetailsContainer
          displayInitialLoader={displayLoader}
          companyId={detailsData.id}
          contentHeight={
            fromMd
              ? companyDetailsContentHeight
              : `calc(${detailsContentHeightToMd} - ${tabsAreaHeight})`
          }
          details={
            <>
              {renderGeneralDetails(detailsData)}
              {renderCompanySettings(detailsData)}
              {renderCompanyCards(detailsData, displayLoader)}
            </>
          }
          fileSyncContent={renderFileSyncContent()}
          emailTransferContent={renderEmailTransferContent}
        />
      )}

      {!companiesPage && (
        <div
          css={css({
            height: calculateDetailsContentHeight(),
            overflowY: "scroll"
          })}
        >
          {shouldRenderActivateBanner &&
            renderActivateVehicleBanner(detailsData)}
          {renderGeneralDetails(detailsData)}
          {renderConnectionSettings(detailsData)}
          {renderSchedules(detailsData, displayLoader)}
        </div>
      )}
    </div>
  );
};

export default DetailsContent;
